import {useQuery} from '@apollo/client'
import React, {useEffect} from 'react'
import {GET_ALL_LOGS} from '../../../gql/queries/policiesApiQueries'
import {toast} from 'react-toastify'
import {Accordion, Spinner} from 'react-bootstrap'
import moment from 'moment'
import {formatPhoneDisplay} from 'utils/phoneUtils'
import {PhoneProviderActions} from '../../../components/telephony/PhoneProviderActions'

interface IActivityProps {
  id: string
  isQuote?: boolean
}

interface IResLogsPolicy {
  policyLogsByPolicyId: {
    id: string
    oldValue: string
    newValue: string
    updatedAt: string
    user: {
      id: string
      firstName: string
      lastName: string
    }
    changes: string[]
  }[]
}

const toDict = (val: any) => {
  if (val == null) return {}
  if (typeof val === 'string') {
    try {
      return JSON.parse(val)
    } catch (e) {
      return val // return raw string so we can detect plain-message
    }
  }
  if (typeof val === 'object') return val
  return {}
}

const isPlainMessage = (oldVal: any, newVal: any) => {
  // plain message if either side is a non-JSON string
  return (typeof oldVal === 'string' && oldVal.trim() !== '') || (typeof newVal === 'string' && newVal.trim() !== '')
}

type DifferenceRow = {
  field: string
  oldValue: any
  newValue: any
  meta?: {
    type: 'member'
    changedKeys: string[]
  }
}

const currencyFormatter = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  minimumFractionDigits: 2,
})

const formatCurrency = (value: any) => {
  const number = Number(value)
  if (Number.isFinite(number)) {
    return currencyFormatter.format(number)
  }
  return value ?? 'N/A'
}

const ensureObject = (value: any) => {
  if (!value) return undefined
  if (typeof value === 'object') return value
  if (typeof value === 'string') {
    try {
      return JSON.parse(value)
    } catch (err) {
      return undefined
    }
  }
  return undefined
}

const memberFieldLabels: Record<string, string> = {
  first_name: 'First Name',
  middle_name: 'Middle Name',
  last_name: 'Last Name',
  second_last_name: 'Second Last Name',
  rol: 'Role',
  relationship: 'Relationship',
  applicant: 'Applicant',
  gender: 'Gender',
  date_birth: 'Date of Birth',
  phone: 'Phone',
  email_address: 'Email',
  country_birth: 'Country of Birth',
  marital_status: 'Marital Status',
  annual_income1: 'Annual Income 1',
  annual_income2: 'Annual Income 2',
  annual_income3: 'Annual Income 3',
  ssn: 'SSN',
  uses_tobacco: 'Uses Tobacco',
  immigration_status: 'Immigration Status',
  immigration_status_code: 'Immigration Status Code',
  employer_name1: 'Employer Name 1',
  employer_name2: 'Employer Name 2',
  employer_name3: 'Employer Name 3',
  employer_phone1: 'Employer Phone 1',
  employer_phone2: 'Employer Phone 2',
  employer_phone3: 'Employer Phone 3',
  position_occupation1: 'Position/Occupation 1',
  position_occupation2: 'Position/Occupation 2',
  position_occupation3: 'Position/Occupation 3',
  annual_income: 'Annual Income',
}

const humanizeKey = (key: string) => {
  return key
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (char) => char.toUpperCase())
}

const formatMemberFieldValue = (key: string, value: any) => {
  if (value === null || value === undefined) return 'N/A'
  if (value === '') return '—'
  if (typeof value === 'boolean') return value ? 'Yes' : 'No'
  if (typeof value === 'number') {
    if (key.includes('income')) return formatCurrency(value)
    return value.toString()
  }
  if (typeof value === 'string') {
    if (key.includes('phone')) return formatPhoneDisplay(value)
    if (key.includes('date')) {
      return moment(value).isValid() ? moment(value).format('MM/DD/YYYY') : value
    }
    return value
  }
  return JSON.stringify(value, null, 2)
}

const areValuesEqual = (valueA: any, valueB: any) => {
  if (valueA === valueB) return true
  if (valueA === null || valueA === undefined || valueB === null || valueB === undefined) {
    return valueA === valueB
  }
  if (typeof valueA === 'object' && typeof valueB === 'object') {
    try {
      return JSON.stringify(valueA) === JSON.stringify(valueB)
    } catch (err) {
      return false
    }
  }
  return false
}

const getMemberChangedKeys = (oldMember: any, newMember: any) => {
  const oldParsed = ensureObject(oldMember) || {}
  const newParsed = ensureObject(newMember) || {}
  if (typeof oldParsed !== 'object' || typeof newParsed !== 'object') return []
  const keys = new Set([...Object.keys(oldParsed), ...Object.keys(newParsed)])
  const changed: string[] = []
  keys.forEach((key) => {
    const prev = (oldParsed as Record<string, any>)[key]
    const next = (newParsed as Record<string, any>)[key]
    if (!areValuesEqual(prev, next)) {
      changed.push(key)
    }
  })
  return changed
}

const getMemberDisplayName = (member: any) => {
  const parsedMember = ensureObject(member)
  if (parsedMember && typeof parsedMember === 'object') {
    const name = [
      parsedMember.first_name,
      parsedMember.middle_name,
      parsedMember.last_name,
      parsedMember.second_last_name,
    ]
      .filter(Boolean)
      .join(' ')
    if (name) return name
    if (parsedMember.relationship) return `Member (${parsedMember.relationship})`
    if (parsedMember.id) return `Member ${parsedMember.id}`
  }
  return 'Member'
}

const buildMemberChangeSummary = (diff: DifferenceRow) => {
  const changedKeys = diff.meta?.changedKeys ?? []
  const labelList = changedKeys
    .map((key) => memberFieldLabels[key] ?? humanizeKey(key))
    .filter(Boolean)
  const uniqueLabels = Array.from(new Set(labelList))
  const name = getMemberDisplayName(diff.newValue ?? diff.oldValue)
  if (uniqueLabels.length === 0) {
    return `Changes in member ${name}`
  }
  return `Changes in member ${name}: ${uniqueLabels.join(', ')}`
}

const formatChangeMessages = (messages: string[] | undefined, memberDiffs: DifferenceRow[]) => {
  if (!Array.isArray(messages)) return []
  const diffsQueue = [...memberDiffs]
  const formatted = messages.map((msg) => {
    if (typeof msg === 'string' && msg.toLowerCase().includes('member') && diffsQueue.length > 0) {
      const diff = diffsQueue.shift()!
      return buildMemberChangeSummary(diff)
    }
    return msg
  })

  while (diffsQueue.length > 0) {
    formatted.push(buildMemberChangeSummary(diffsQueue.shift()!))
  }

  return formatted.filter((msg) => typeof msg === 'string' && msg.trim() !== '')
}

const formatMemberSummary = (member: any, changedKeys?: string[]) => {
  const parsedMember = ensureObject(member)
  const changeSet = changedKeys && changedKeys.length > 0 ? new Set(changedKeys) : undefined

  if ((!parsedMember || typeof parsedMember !== 'object') && changeSet) {
    return (
      <div className='d-flex flex-column gap-1'>
        {Array.from(changeSet).map((key) => (
          <div key={key}>
            <strong>{memberFieldLabels[key] ?? humanizeKey(key)}:</strong> N/A
          </div>
        ))}
      </div>
    )
  }

  if (!parsedMember || typeof parsedMember !== 'object') {
    return parsedMember ? String(parsedMember) : 'N/A'
  }

  const handledKeys = new Set<string>()
  const fullName = [
    parsedMember.first_name,
    parsedMember.middle_name,
    parsedMember.last_name,
    parsedMember.second_last_name,
  ]
    .filter(Boolean)
    .join(' ')

  const items: {label: string; value: React.ReactNode}[] = []

  const addItem = (keys: string | string[], label: string, value: React.ReactNode) => {
    if (value === null || value === undefined || value === '' || value === 'N/A') return
    const keyArray = Array.isArray(keys) ? keys : [keys]
    if (changeSet && !keyArray.some((key) => changeSet.has(key))) return
    keyArray.forEach((key) => handledKeys.add(key))
    items.push({label, value})
  }

  addItem(['first_name', 'middle_name', 'last_name', 'second_last_name'], 'Name', fullName)
  addItem('rol', 'Role', parsedMember.rol)
  addItem('relationship', 'Relationship', parsedMember.relationship)
  if (typeof parsedMember.applicant === 'boolean') {
    addItem('applicant', 'Applicant', parsedMember.applicant ? 'Yes' : 'No')
  }
  addItem('gender', 'Gender', parsedMember.gender)
  if (parsedMember.date_birth) {
    addItem(
      'date_birth',
      'Date of Birth',
      moment(parsedMember.date_birth).isValid()
        ? moment(parsedMember.date_birth).format('MM/DD/YYYY')
        : parsedMember.date_birth
    )
  }
  addItem(
    'phone',
    'Phone',
    parsedMember.phone ? (
      <PhoneProviderActions
        phone={parsedMember.phone}
        displayText={formatPhoneDisplay(parsedMember.phone)}
        className='d-inline-flex flex-column align-items-start'
      />
    ) : null
  )
  addItem('email_address', 'Email', parsedMember.email_address)
  addItem('country_birth', 'Country of Birth', parsedMember.country_birth)
  addItem('marital_status', 'Marital Status', parsedMember.marital_status)

  ;['annual_income1', 'annual_income2', 'annual_income3'].forEach((key, idx) => {
    const amount = parsedMember[key]
    const formatted = formatCurrency(amount)
    if (!changeSet && (formatted === 'N/A' || formatted === '$0.00')) {
      return
    }
    addItem(key, `Annual income ${idx + 1}`, formatted)
  })

  if (changeSet) {
    changeSet.forEach((key) => {
      if (handledKeys.has(key)) return
      const label = memberFieldLabels[key] ?? humanizeKey(key)
      const rawValue = (parsedMember as Record<string, any>)[key]
      items.push({
        label,
        value: formatMemberFieldValue(key, rawValue),
      })
    })
  }

  if (items.length === 0) {
    return <span>{JSON.stringify(parsedMember, null, 2)}</span>
  }

  return (
    <div className='d-flex flex-column gap-1'>
      {items.map(({label, value}) => (
        <div key={label}>
          <strong>{label}:</strong> {value}
        </div>
      ))}
    </div>
  )
}

const renderValue = (field: string, value: any, meta?: DifferenceRow['meta']): React.ReactNode => {
  if (meta?.type === 'member') {
    return formatMemberSummary(value, meta.changedKeys)
  }

  if (value === null || value === undefined) return 'N/A'
  if (typeof value === 'boolean') return value ? 'Yes' : 'No'
  if (typeof value === 'object') {
    return <pre className='mb-0'>{JSON.stringify(value, null, 2)}</pre>
  }

  return String(value)
}

export const ActivityTab: React.FC<IActivityProps> = ({id, isQuote}) => {
  const {data, loading, error} = useQuery<IResLogsPolicy>(GET_ALL_LOGS, {
    variables: {policyId: Number(id)},
    fetchPolicy: 'no-cache',
  })

  useEffect(() => {
    if (error) toast.error(`Error: ${error.message}`)
  }, [error])

  if (loading || !data) {
    return (
      <div className='cardContainer'>
        <div className='kt-heading pb-2 custom-border-brand d-flex align-items-center justify-content-between'>
          <span>Activity</span>
        </div>
        <div className='d-flex flex-center' style={{height: '40vh'}}>
          <Spinner animation='border' />
        </div>
      </div>
    )
  }

  if (data.policyLogsByPolicyId.length === 0) {
    return (
      <div className='cardContainer'>
        <div className='kt-heading pb-2 custom-border-brand d-flex align-items-center justify-content-between'>
          <span>Activity</span>
        </div>
        <div className='d-flex flex-center' style={{height: '40vh'}}>
          <h4>There is no activity recorded at the moment</h4>
        </div>
      </div>
    )
  }

  const first = data.policyLogsByPolicyId[0]

  const restArr = data.policyLogsByPolicyId?.slice(1) || []

  return (
    <div className='policy-activity'>
      <div className='kt-heading kt-heading--md custom-border-brand'>
        {isQuote && <span>Quote logs</span>}
        {!isQuote && <span>Policy logs</span>}
      </div>
      <Accordion defaultActiveKey={`${data.policyLogsByPolicyId.length}`}>
        {restArr.reverse().map((log, index) => {
          const {oldValue, newValue, updatedAt, user, changes} = log
          const oldParsed = toDict(oldValue)
          const newParsed = toDict(newValue)

          let differences: DifferenceRow[] = []

          if (!isPlainMessage(oldParsed, newParsed)) {
            const newObj = (typeof newParsed === 'object' && newParsed) ? newParsed as Record<string, any> : {}
            const oldObj = (typeof oldParsed === 'object' && oldParsed) ? oldParsed as Record<string, any> : {}
            const allKeys = new Set([...Object.keys(oldObj), ...Object.keys(newObj)])

            // Detectar si advisor_paid cambió de false a true
            const advisorPaidChanged = oldObj['advisor_paid'] === false && newObj['advisor_paid'] === true

            differences = Array.from(allKeys).reduce((diff, key) => {
              const prev = oldObj[key]
              const next = newObj[key]

              if (areValuesEqual(prev, next)) {
                // Si advisor_paid cambió y este es el campo assigned_to, agregarlo aunque no haya cambiado
                if (advisorPaidChanged && key === 'assigned_to' && next) {
                  diff.push({
                    field: key,
                    oldValue: prev,
                    newValue: next,
                  })
                }
                return diff
              }

              if (key === 'member') {
                const changedMemberKeys = getMemberChangedKeys(prev, next)
                if (changedMemberKeys.length === 0) return diff
                diff.push({
                  field: key,
                  oldValue: prev,
                  newValue: next,
                  meta: {type: 'member', changedKeys: changedMemberKeys},
                })
                return diff
              }

              diff.push({
                field: key,
                oldValue: prev,
                newValue: next,
              })
              return diff
            }, [] as DifferenceRow[])

            differences = differences.filter((ob) => ob.field !== 'updated')
          }

          const memberDiffs = differences.filter((diff) => diff.meta?.type === 'member')
          const formattedChanges = formatChangeMessages(changes, memberDiffs)

          return (
            <Accordion.Item
              eventKey={`${data.policyLogsByPolicyId.length - index}`}
              key={log.id}
              className='log-entry'
            >
              <Accordion.Header className='fw-bold'>
                <span>{data.policyLogsByPolicyId.length - index}:</span>&nbsp;{' '}
                <span> {moment(updatedAt).format('YYYY-MM-DD HH:mm:ss')} </span>&nbsp;-&nbsp;
                <span className='fw-bold'>
                  {' '}
                  {user.firstName} {user.lastName}
                </span>
              </Accordion.Header>
              <Accordion.Body>
                <p>
                  {/* <strong>Updated by:</strong> {user.firstName} {user.lastName} <br /> */}
                  {/* <strong>Date:</strong> {new Date(updatedAt).toLocaleString()} <br />  */}
                  <strong>Changes:</strong>{' '}
                  {formattedChanges.length > 0 ? formattedChanges.join(', ') : (Array.isArray(changes) ? changes.join(', ') : '—')}
                </p>
                {isPlainMessage(toDict(oldValue), toDict(newValue)) ? (
                  <div className='text-muted'>No structured changes to display.</div>
                ) : (
                  <table className='table table-striped'>
                    <thead>
                      <tr>
                        <th className='fw-bold'>Field</th>
                        <th className='fw-bold'>Previous Value</th>
                        <th className='fw-bold'>New Value</th>
                      </tr>
                    </thead>
                    <tbody>
                      {differences.length > 0 ? (
                        differences.map(({field, oldValue, newValue, meta}, i) => (
                          <tr key={i}>
                            <td>{field}</td>
                            <td>{renderValue(field, oldValue, meta)}</td>
                            <td>{renderValue(field, newValue, meta)}</td>
                          </tr>
                        ))
                      ) : (
                        <tr>
                          <td colSpan={3}>There are no differences</td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                )}
              </Accordion.Body>
            </Accordion.Item>
          )
        })}
        <Accordion.Item eventKey={`${1}`} className='log-entry'>
          <Accordion.Header className='font-bold'>
            <span>{1}:</span>&nbsp; {moment(first.updatedAt).format('YYYY-MM-DD HH:mm:ss')} - &nbsp;
            <span className='fw-bold'>
              {' '}
              {first.user.firstName} {first.user.lastName}
            </span>{' '}
          </Accordion.Header>
          <Accordion.Body>
            <p>
              <strong>Changes:</strong> {Array.isArray(first.changes) ? first.changes.join(', ') : String(first.changes || '—')}
            </p>
          </Accordion.Body>
        </Accordion.Item>
      </Accordion>
    </div>
  )
}
